import { useCallback } from 'react';
import type { RequestPolicy } from 'urql';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDeliveryInFlightQuery } from './DeliveryInFlight.generated';

export const useDeliveryOrderInFlight = (
  props?: UseDeliveryOrderInFlightProps,
) => {
  const { pause, requestPolicy = 'cache-and-network' } = props ?? {};
  const isLoggedIn = useIsLoggedIn();

  // ─── Delivery Order In Flight Query ─────────────────────────────
  const [response] = useDeliveryInFlightQuery({
    variables: { input: { page: 1, take: 3 } },
    requestPolicy,
    pause: Boolean(pause) || !isLoggedIn,
  });

  // ─── Delivery Order In Flight Id ────────────────────────────────
  const ordersResponseData = response.data?.orders;
  const isSuccess = ordersResponseData?.__typename === 'OrdersResponseSuccess';
  const orders = isSuccess ? ordersResponseData.orders : [];
  const inFlightDeliveryOrderId = orders.find(
    ({ isInFlightDelivery }) => isInFlightDelivery,
  )?.id;

  // ─── Navigate To Delivery Order In Flight Screen ────────────────
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();
  const navigation = useSafeNavigation();
  const navigateToDeliveryOrderInFlightScreen = useCallback(() => {
    if (!inFlightDeliveryOrderId) return;

    if (isExpoRouterEnabled) {
      router?.navigate({ pathname: '/delivery-order-in-flight', params: { orderId: inFlightDeliveryOrderId } }); // prettier-ignore
      return;
    }

    navigation?.navigate('Modal', {
      screen: 'DeliveryOrderInFlight',
      params: { orderId: inFlightDeliveryOrderId },
    });
  }, [inFlightDeliveryOrderId, isExpoRouterEnabled, router, navigation]);

  return { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen };
};

type UseDeliveryOrderInFlightProps = Readonly<{
  requestPolicy?: RequestPolicy;
  pause?: boolean;
}>;
